/* =========================================================
   POPOSOAP — Brand Story Carousel (section behavior) v2
   Transform-based carousel engine (Swiper/Embla-style):
   translateX track + clones + instant position normalization.

   WHY NOT NATIVE SCROLL: scripted scrolling on scroll-snap
   containers is broken in WebKit (bugs 283707 / 293227) and
   `scrollend` support is still inconsistent — native scroll +
   infinite loop is the industry-known fragile combination.
   A transform engine is deterministic in every mainstream
   browser and never fights momentum/snap behavior.

   - One <brand-story-carousel> custom element per section
     instance; every instance owns its full state.
   - No-JS baseline: fixed background + visible cards (the
     viewport hides overflow; arrows stay hidden via `hidden`).
   - Infinite loop: measured clone sets + seamless congruent
     normalization (no rewind, no duplicate IDs/tab stops;
     clones are aria-hidden + inert).
   - Autoplay pauses on: hover, keyboard focus, drag/touch,
     hidden tab, offscreen (IntersectionObserver), reduced
     motion, and Theme Editor block selection.
   - Touch: pointer-events drag with horizontal intent lock
     (touch-action: pan-y keeps vertical page scroll native).
   ========================================================= */
(function () {
  'use strict';

  if (!window.customElements || window.customElements.get('brand-story-carousel')) {
    return;
  }

  var REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)');
  var DRAG_THRESHOLD = 8; // px of horizontal intent before a drag starts

  var BrandStoryCarousel = class extends HTMLElement {
    constructor() {
      super();
      this._abort = null;
      this._timer = 0;
      this._settleTimer = 0;
      this._raf = 0;
      this._wheelTimer = 0;
      this._initAttempts = 0;
      this._cards = [];
      this._clones = [];
      this._index = 0;
      this._realCard = 0;
      this._pos = 0;
      this._interval = 0;
      this._loop = false;
      this._overflow = false;
      this._hovered = false;
      this._focused = false;
      this._touching = false;
      this._inView = true;
      this._hidden = false;
      this._editingBlock = false;
      this._animating = false;
      this._targetPos = -1;
      this._drag = null;
      this._suppressClick = false;
      this._wheelAcc = 0;
      this._reducedMotion = REDUCED_MOTION.matches;
      this._isRTL = false;
    }

    connectedCallback() {
      if (this._abort) {
        return; // already initialized
      }

      this._abort = new AbortController();
      var signal = this._abort.signal;

      this._viewport = this.querySelector('[data-bsc-viewport]');
      this._track = this.querySelector('[data-bsc-track]');
      this._prevBtn = this.querySelector('[data-bsc-prev]');
      this._nextBtn = this.querySelector('[data-bsc-next]');
      this._cards = Array.prototype.slice.call(this.querySelectorAll('[data-bsc-card]'));
      this._clones = [];

      if (!this._viewport || !this._track) {
        return;
      }

      this._isRTL = getComputedStyle(this._viewport).direction === 'rtl';
      this._loop = this.hasAttribute('loop');
      this._interval = parseInt(this.getAttribute('autoplay-interval') || '0', 10) || 0;

      // Buttons
      this._prevBtn && this._prevBtn.addEventListener('click', function () {
        this._navigate(-1);
        this._armTimer();
      }.bind(this), { signal: signal });
      this._nextBtn && this._nextBtn.addEventListener('click', function () {
        this._navigate(1);
        this._armTimer();
      }.bind(this), { signal: signal });

      // Autoplay pause: hover
      this.addEventListener('pointerenter', function () {
        this._hovered = true;
        this._armTimer();
      }.bind(this), { signal: signal });
      this.addEventListener('pointerleave', function () {
        this._hovered = false;
        this._armTimer();
      }.bind(this), { signal: signal });

      // Autoplay pause + drag start
      this._viewport.addEventListener('pointerdown', function (event) {
        this._touching = true;
        this._armTimer();
        if (event.button !== undefined && event.button !== 0) {
          return; // only primary button
        }
        if (event.target.closest && event.target.closest('a, button, input, select, textarea')) {
          return; // let links/controls behave natively
        }
        this._drag = {
          id: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          pos: this._pos,
          active: false,
          moved: false
        };
        if (event.pointerType !== 'mouse') {
          try {
            this._viewport.setPointerCapture(event.pointerId);
          } catch (err) { /* no-op */ }
        }
      }.bind(this), { signal: signal });

      this._onPointerMove = function (event) {
        var d = this._drag;
        if (!d || event.pointerId !== d.id) {
          return;
        }
        var dx = event.clientX - d.startX;
        var dy = event.clientY - d.startY;
        if (!d.active) {
          if (Math.abs(dy) > DRAG_THRESHOLD && Math.abs(dy) > Math.abs(dx) * 1.2) {
            this._drag = null; // vertical intent: let the page scroll natively
            return;
          }
          if (Math.abs(dx) > DRAG_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
            d.active = true;
            this._track.classList.add('is-dragging');
            this._track.classList.remove('is-animating');
            this._animating = false;
          } else {
            return;
          }
        }
        d.moved = d.moved || Math.abs(dx) > DRAG_THRESHOLD;
        this._setPosRaw(Math.max(0, Math.min(d.pos - dx, this._maxScroll)));
      }.bind(this);
      window.addEventListener('pointermove', this._onPointerMove, { signal: signal });

      this._onPointerEnd = function (event) {
        var d = this._drag;
        if (!d || event.pointerId !== d.id) {
          return;
        }
        this._drag = null;
        this._track.classList.remove('is-dragging');
        if (event.type === 'pointercancel') {
          // The browser took over (vertical scroll): return to the committed card.
          this._setPos(this._items()[this._index].offsetLeft, false);
          this._touching = false;
          this._armTimer();
          return;
        }
        if (!d.active) {
          this._touching = false;
          this._armTimer();
          return;
        }
        if (d.moved) {
          this._suppressClick = true;
          clearTimeout(this._settleTimer);
          this._settleTimer = setTimeout(function () {
            this._suppressClick = false;
          }.bind(this), 400);
        }
        var target = d.pos - (event.clientX - d.startX);
        var index = this._indexFromPos(target);
        // Dragging past the seamless-wrap bound: land on the congruent position
        // in the real set instead of resting in the tail scenery.
        if (this._loop && !this._isRTL && this._clones.length) {
          var spacerOffset = this._spacerOffset();
          var n = this._cards.length;
          var seamless = this._currentMaxIndex();
          if (index > seamless && n) {
            index = ((index - spacerOffset) % n + n) % n + spacerOffset;
          }
        }
        this._scrollToItem(index, true); // settle re-arms the autoplay timer
      }.bind(this);
      window.addEventListener('pointerup', this._onPointerEnd, { signal: signal });
      window.addEventListener('pointercancel', this._onPointerEnd, { signal: signal });

      // A drag must never activate a card link
      this._viewport.addEventListener('click', function (event) {
        if (this._suppressClick) {
          event.preventDefault();
          event.stopPropagation();
          this._suppressClick = false;
        }
      }.bind(this), { signal: signal, capture: true });

      // Trackpad: horizontal gestures advance the carousel; vertical stays native
      this._viewport.addEventListener('wheel', function (event) {
        var dx = event.deltaX || (event.shiftKey ? event.deltaY : 0);
        var dy = event.shiftKey ? 0 : event.deltaY;
        if (Math.abs(dx) < 6 || Math.abs(dx) <= Math.abs(dy)) {
          return;
        }
        event.preventDefault(); // horizontal intent only
        if (!this._overflow || this._reducedMotion) {
          return;
        }
        this._wheelAcc += dx;
        clearTimeout(this._wheelTimer);
        this._wheelTimer = setTimeout(function () {
          this._wheelAcc = 0;
        }.bind(this), 150);
        var step = Math.max(1, this._step);
        while (this._wheelAcc >= step * 0.5) {
          this._wheelAcc -= step;
          this._navigate(1);
        }
        while (this._wheelAcc <= -step * 0.5) {
          this._wheelAcc += step;
          this._navigate(-1);
        }
        this._armTimer();
      }.bind(this), { signal: signal, passive: false });

      // Track transition completion
      this._track.addEventListener('transitionend', function (event) {
        if (event.target === this._track && event.propertyName === 'transform') {
          this._onSettle();
        }
      }.bind(this), { signal: signal });

      // Autoplay pause: keyboard focus (+ bring the focused card into view)
      this.addEventListener('focusin', function (event) {
        this._focused = true;
        this._armTimer();
        var card = event.target.closest && event.target.closest('[data-bsc-card]');
        if (card && !card.hasAttribute('data-bsc-clone') && this._cards.indexOf(card) !== -1) {
          this._scrollToItem(this._items().indexOf(card), true);
        }
      }.bind(this), { signal: signal });
      this.addEventListener('focusout', function (event) {
        if (!this.contains(event.relatedTarget)) {
          this._focused = false;
          this._armTimer();
        }
      }.bind(this), { signal: signal });

      // Tab visibility
      this._hidden = document.hidden;
      document.addEventListener('visibilitychange', function () {
        this._hidden = document.hidden;
        this._armTimer();
      }.bind(this), { signal: signal });

      // Reduced motion changes
      REDUCED_MOTION.addEventListener('change', function (event) {
        this._reducedMotion = event.matches;
        if (event.matches) {
          this._track.classList.remove('is-animating');
          this._animating = false;
        }
        this._armTimer();
      }.bind(this), { signal: signal });

      // Section visibility (autoplay only while meaningfully on screen)
      if ('IntersectionObserver' in window) {
        this._io = new IntersectionObserver(function (entries) {
          this._inView = entries[entries.length - 1].isIntersecting;
          this._armTimer();
        }.bind(this), { threshold: 0.25 });
        this._io.observe(this);
      } else {
        this._inView = true;
      }

      // Geometry changes: resize, orientation, Theme Editor sidebar
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(this._scheduleResize.bind(this));
        this._ro.observe(this._viewport);
        this._ro.observe(this);
      }
      window.addEventListener('resize', this._scheduleResize.bind(this), { signal: signal });
      window.addEventListener('orientationchange', this._scheduleResize.bind(this), { signal: signal });

      // Theme Editor: pause autoplay while editing + bring the selected card into view
      if (window.Shopify && Shopify.designMode) {
        this.addEventListener('shopify:block:select', function (event) {
          this._editingBlock = true;
          this._armTimer();
          var blockId = event.detail && event.detail.blockId;
          if (!blockId) {
            return;
          }
          var card = this._cards.find(function (item) {
            var attr = item.getAttribute('data-shopify-editor-block');
            if (!attr) {
              return false;
            }
            try {
              return JSON.parse(attr).id === blockId;
            } catch (err) {
              return attr.indexOf(String(blockId)) !== -1;
            }
          });
          if (card) {
            this._scrollToItem(this._items().indexOf(card), true);
          }
        }.bind(this), { signal: signal });
        this.addEventListener('shopify:block:deselect', function () {
          this._editingBlock = false;
          this._armTimer();
        }.bind(this), { signal: signal });
      }

      // Measure after layout (CSS may arrive just after the editor inserts the section)
      this._initialize();

      // Second-chance initialization once the full page (fonts, apps, late CSS)
      // has settled: re-measures and rebuilds the loop clones if the first pass
      // was premature (e.g. slow CDN CSS on the real storefront).
      window.addEventListener('load', function () {
        if (this._abort && this._viewport) {
          this._initialize();
        }
      }.bind(this), { signal: signal, once: true });
    }

    disconnectedCallback() {
      if (!this._abort) {
        return;
      }
      this._abort.abort();
      this._abort = null;
      clearTimeout(this._timer);
      this._timer = 0;
      clearTimeout(this._settleTimer);
      this._settleTimer = 0;
      clearTimeout(this._wheelTimer);
      this._wheelTimer = 0;
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = 0;
      }
      if (this._io) {
        this._io.disconnect();
        this._io = null;
      }
      if (this._ro) {
        this._ro.disconnect();
        this._ro = null;
      }
      this._clones.forEach(function (clone) {
        clone.remove();
      });
      this._clones = [];
    }

    /* ------------------------------------------------------ helpers */

    _scenicEl() {
      // The scenic spacer only exists as a logical item while it is rendered
      // (desktop). On mobile it is display:none and card 01 IS the start.
      var scenic = this.querySelector('[data-bsc-scenic]');
      return scenic && getComputedStyle(scenic).display !== 'none' ? scenic : null;
    }

    _spacerOffset() {
      return this._scenicEl() ? 1 : 0;
    }

    _items() {
      var items = [];
      var scenic = this._scenicEl();
      if (scenic) {
        items.push(scenic);
      }
      return items.concat(this._cards, this._clones);
    }

    _measure() {
      this._viewportWidth = this._viewport.clientWidth;
      if (this._cards.length >= 2) {
        this._step = this._cards[1].offsetLeft - this._cards[0].offsetLeft;
      } else if (this._cards.length === 1) {
        var gap = parseFloat(getComputedStyle(this._track).columnGap) || 0;
        this._step = this._cards[0].offsetWidth + gap;
      } else {
        this._step = 0;
      }
      this._maxScroll = Math.max(0, this._track.scrollWidth - this._viewportWidth);
    }

    _initialize() {
      if (!this._abort || !this._viewport) {
        return;
      }
      this._measure();
      if (this._cards.length && this._step <= 0 && this._initAttempts < 20) {
        // Styles not applied yet (e.g. editor just inserted the section): retry shortly.
        this._initAttempts += 1;
        this._raf = requestAnimationFrame(this._initialize.bind(this));
        return;
      }
      this._initAttempts = 0;

      var firstPass = !this._initDone;
      this._initDone = true;

      this._overflow = this._maxScroll > 1;
      if (this._overflow && this._loop && !this._isRTL) {
        // Rebuild clones only when needed: a late re-pass (window load) must not
        // reshuffle the track under an already-working carousel.
        if (firstPass || !this._clones.length) {
          this._buildClones();
        }
      }
      this._syncTrailingSpacer();
      this._maxScroll = Math.max(0, this._track.scrollWidth - this._viewportWidth);

      if (!firstPass) {
        // Late re-pass: keep the logical index aligned with the measured position.
        var index = this._indexFromPos(this._pos);
        if (index !== this._index) {
          this._setIndex(index);
        }
      }

      this._setPosRaw(this._items()[this._index] ? this._items()[this._index].offsetLeft : 0);
      this._syncState();
      this._armTimer();
    }

    _syncTrailingSpacer() {
      // Trailing scenic spacer: lets the last card reach the viewport's left edge
      // in non-loop mode (mirrors the leading reveal). Presentation only.
      if (!this._track) {
        return;
      }
      var existing = this._track.querySelector('.brand-story-scenic--end');
      if (this._overflow) {
        if (!existing) {
          var spacer = document.createElement('div');
          spacer.className = 'brand-story-scenic brand-story-scenic--end';
          spacer.setAttribute('aria-hidden', 'true');
          this._track.appendChild(spacer);
          existing = spacer;
        }
        // Explicit px sizing: a percentage flex-basis would resolve against the
        // track's CONTENT box, which changes with the theme's global box-sizing.
        var cardW = this._cards.length ? this._cards[0].offsetWidth : 0;
        var gap = this._step > 0 ? this._step - cardW : 0;
        if (cardW > 0) {
          existing.style.flexBasis = Math.max(0, this._viewport.clientWidth - cardW - gap) + 'px';
        }
      } else if (existing) {
        existing.remove();
      }
    }

    _buildClones() {
      this._clones.forEach(function (clone) {
        clone.remove();
      });
      this._clones = [];

      var n = this._cards.length;
      if (!n || this._step <= 0) {
        return;
      }
      // Enough clone sets so that a full viewport window always has valid content
      // around the seamless wrap position.
      var viewportSteps = Math.max(1, Math.ceil(this._viewportWidth / this._step));
      var required = n + viewportSteps + 1;
      var total = n * Math.ceil(required / n);
      var needed = total - n;
      // Clones must always sit right after the real cards — BEFORE the trailing
      // scenic spacer — otherwise the track order (and the wrap math) breaks.
      var trailing = this._track.querySelector('.brand-story-scenic--end');

      for (var i = 0; i < needed; i++) {
        var source = this._cards[i % n];
        var clone = source.cloneNode(true);
        clone.removeAttribute('id');
        Array.prototype.slice.call(clone.attributes).forEach(function (attr) {
          if (/^data-shopify/i.test(attr.name)) {
            clone.removeAttribute(attr.name); // keep Theme Editor block selection unambiguous
          }
        });
        clone.setAttribute('aria-hidden', 'true');
        clone.setAttribute('data-bsc-clone', '');
        clone.setAttribute('inert', '');
        clone.querySelectorAll('[id]').forEach(function (el) {
          el.removeAttribute('id');
        });
        clone.querySelectorAll('a, button, input, select, textarea, [tabindex]').forEach(function (el) {
          el.setAttribute('tabindex', '-1'); // clones never join the keyboard tab order
        });
        if (trailing) {
          this._track.insertBefore(clone, trailing);
        } else {
          this._track.appendChild(clone);
        }
        this._clones.push(clone);
      }
    }

    _currentMaxIndex() {
      // The LAST index from which a forward step may glide normally. Beyond it,
      // the visible window would run past the clone runway (into the trailing
      // scenery), so the seamless wrap must happen here — this makes the visible
      // sequence continuous (C1..C6, C1, C2, ...) instead of "two rounds + restart".
      var items = this._items();
      if (!items.length) {
        return 0;
      }
      var last = items.length - 1;
      var visible = Math.max(1, Math.ceil(this._viewportWidth / Math.max(1, this._step)));
      var seamless = Math.max(0, last - visible + 1);
      var reachable = last;
      for (var i = 0; i <= last; i++) {
        if (items[i].offsetLeft > this._maxScroll + 1) {
          reachable = i - 1;
          break;
        }
      }
      return Math.min(seamless, reachable);
    }

    _indexFromPos(pos) {
      var items = this._items();
      if (!items.length) {
        return 0;
      }
      var best = 0;
      var bestDist = Infinity;
      for (var i = 0; i < items.length; i++) {
        var dist = Math.abs(items[i].offsetLeft - pos);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }
      return best;
    }

    _setPosRaw(pos) {
      pos = Math.max(0, Math.min(pos, this._maxScroll));
      this._pos = pos;
      this._track.style.transform = 'translate3d(' + (-pos) + 'px, 0, 0)';
      this._viewport.dataset.bscPos = String(Math.round(pos * 100) / 100);
    }

    _setPos(pos, animate) {
      this._targetPos = pos;
      if (animate && !this._reducedMotion) {
        this._animating = true;
        this._track.classList.add('is-animating');
      } else {
        this._track.classList.remove('is-animating');
        this._animating = false;
      }
      this._setPosRaw(pos);
      if (!this._animating) {
        this._onSettle();
      } else {
        clearTimeout(this._settleTimer);
        this._settleTimer = setTimeout(this._onSettle.bind(this), 700); // transitionend fallback
      }
    }

    _setIndex(index) {
      this._index = index;
      var n = this._cards.length;
      this._realCard = n ? ((index - this._spacerOffset()) % n + n) % n : 0;
    }

    _scrollToItem(index, smooth) {
      var items = this._items();
      var target = items[index];
      if (!target) {
        return false;
      }
      this._setIndex(index);
      this._setPos(target.offsetLeft, smooth && !this._reducedMotion);
      this._syncState();
      return true;
    }

    _navigate(direction) {
      if (!this._overflow || !this._cards.length) {
        return false;
      }

      var n = this._cards.length;
      var spacerOffset = this._spacerOffset();
      var lastReal = spacerOffset + n - 1;

      if (this._isRTL) {
        // RTL: safe linear navigation (no clones, no loop normalization).
        var rtlTarget = this._index + direction;
        if (rtlTarget < 0 || rtlTarget > lastReal) {
          return false;
        }
        return this._scrollToItem(rtlTarget, true);
      }

      if (direction < 0) {
        if (this._index <= 0) {
          return false; // previous is unavailable at the scenic home / first card
        }
        return this._scrollToItem(this._index - 1, true);
      }

      // Forward
      if (!this._loop && this._index >= lastReal) {
        return false;
      }

      // Self-healing: a loop needs clones. If they are missing (premature init,
      // interrupted build, stale measurements), rebuild them before deciding.
      if (this._loop && !this._clones.length && this._step > 0) {
        this._buildClones();
        this._syncTrailingSpacer();
        this._maxScroll = Math.max(0, this._track.scrollWidth - this._viewportWidth);
      }

      var items = this._items();
      var maxIndex = this._currentMaxIndex();
      var target = this._index + 1;

      if (this._loop && target > maxIndex) {
        // Seamless wrap: instantly normalize to the congruent position in the real
        // card set (visually identical window), then glide one card forward.
        var congruent = ((this._index - spacerOffset) % n + n) % n + spacerOffset;
        this._track.classList.remove('is-animating');
        this._animating = false;
        this._setPosRaw(items[congruent].offsetLeft); // instant, no transition
        this._setIndex(congruent);
        target = congruent + 1;
      }

      // Absolute guarantee: never silently stall in loop mode.
      if (this._loop && target > maxIndex && items.length) {
        var fallback = ((target - spacerOffset) % n + n) % n + spacerOffset;
        this._track.classList.remove('is-animating');
        this._animating = false;
        this._setPosRaw(items[Math.min(fallback, items.length - 1)].offsetLeft);
        this._setIndex(fallback);
        target = fallback;
      }

      if (target >= items.length) {
        return false; // physically cannot advance (no more content at all)
      }
      return this._scrollToItem(target, true);
    }

    _syncState() {
      if (!this._prevBtn || !this._nextBtn) {
        return;
      }
      var n = this._cards.length;
      var spacerOffset = this._spacerOffset();
      var lastReal = spacerOffset + n - 1;

      if (!this._overflow || n === 0) {
        this._prevBtn.hidden = true;
        this._nextBtn.hidden = true;
        return;
      }

      this._prevBtn.hidden = false;
      this._nextBtn.hidden = false;
      this._prevBtn.disabled = this._index <= 0;
      this._nextBtn.disabled = (this._isRTL || !this._loop) && this._index >= lastReal;
    }

    /* ------------------------------------------------------ settle */

    _onSettle() {
      clearTimeout(this._settleTimer);
      this._track.classList.remove('is-animating');
      this._animating = false;
      var index = this._indexFromPos(this._pos);
      if (index !== this._index) {
        this._setIndex(index);
        this._syncState();
      }
      this._touching = false;
      this._armTimer(); // fresh full interval after any interaction settles
    }

    /* ------------------------------------------------------ resize / orientation */

    _scheduleResize() {
      if (this._raf) {
        cancelAnimationFrame(this._raf);
      }
      this._raf = requestAnimationFrame(function () {
        this._raf = 0;
        if (!this._abort || !this._viewport) {
          return;
        }

        var keepIndex = 0;
        if (this._index > 0 && this._cards.length) {
          // Preserve the current REAL card across geometry changes (the scenic
          // spacer appears/disappears between breakpoints, so the raw index is
          // not stable — the remembered real-card id is).
          keepIndex = this._spacerOffset() + this._realCard;
        }

        // Remove loop/enhancement artifacts before measuring the real overflow.
        this._clones.forEach(function (clone) {
          clone.remove();
        });
        this._clones = [];
        var trailing = this._track.querySelector('.brand-story-scenic--end');
        if (trailing) {
          trailing.remove();
        }

        this._measure();
        this._overflow = this._maxScroll > 1;
        if (this._overflow && this._loop && !this._isRTL) {
          this._buildClones();
        }
        this._syncTrailingSpacer();
        this._maxScroll = Math.max(0, this._track.scrollWidth - this._viewportWidth);

        var items = this._items();
        var targetIndex = Math.min(keepIndex, items.length - 1);
        this._track.classList.remove('is-animating');
        this._animating = false;
        this._setPosRaw(items[targetIndex] ? items[targetIndex].offsetLeft : 0);
        this._setIndex(targetIndex);

        this._syncState();
        this._armTimer();
      }.bind(this));
    }

    /* ------------------------------------------------------ autoplay */

    _canAutoplay() {
      return (
        this._interval > 0 &&
        this._overflow &&
        !this._isRTL &&
        !this._reducedMotion &&
        !this._hovered &&
        !this._focused &&
        !this._touching &&
        this._inView &&
        !this._hidden &&
        !this._editingBlock
      );
    }

    _armTimer() {
      clearTimeout(this._timer);
      this._timer = 0;
      if (!this._canAutoplay()) {
        return;
      }
      this._timer = setTimeout(function () {
        this._timer = 0;
        if (!this._canAutoplay()) {
          this._armTimer();
          return;
        }
        if (!this._navigate(1)) {
          return; // loop OFF and the last card is reached: autoplay stops
        }
        this._armTimer(); // always a fresh, complete interval
      }.bind(this), this._interval * 1000);
    }
  };

  window.customElements.define('brand-story-carousel', BrandStoryCarousel);
})();
